import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useMemo,
  useEffect,
} from "react";
import { useRouter } from "next/router";

/**
 * Represents a single breadcrumb item in the folder navigation path.
 */
export type FolderBreadcrumb = {
  /** The library item ID of the folder */
  id: string;
  /** The folder ID (from the folder.id field) */
  folderId: string;
  /** The display name of the folder */
  name: string;
};

export interface FolderBreadcrumbContextValue {
  /**
   * The current breadcrumb path, ordered from root (Library) to current folder.
   * Does not include the "Library" item - that should be prepended by the UI.
   */
  breadcrumbs: FolderBreadcrumb[];

  /**
   * Called when navigating to a subfolder. Appends the subfolder to the breadcrumb path.
   */
  navigateToSubfolder: (folder: FolderBreadcrumb) => void;

  /**
   * Called when navigating via a breadcrumb click. Truncates the path to that folder.
   */
  navigateToBreadcrumb: (folderId: string) => void;

  /**
   * Sets the breadcrumbs directly - used when computing path from server data.
   */
  setBreadcrumbs: (breadcrumbs: FolderBreadcrumb[]) => void;

  /**
   * Whether we're in the middle of an in-app navigation vs a direct URL load.
   * When true, we should use the breadcrumb state.
   * When false/undefined, we should compute breadcrumbs from server data.
   */
  hasNavigationContext: boolean;

  /**
   * Clear the navigation context (e.g., when leaving the library section).
   */
  clearNavigationContext: () => void;
}

const FolderBreadcrumbContext =
  createContext<FolderBreadcrumbContextValue | null>(null);

interface FolderBreadcrumbProviderProps {
  children: React.ReactNode;
}

export const FolderBreadcrumbProvider: React.FC<
  FolderBreadcrumbProviderProps
> = ({ children }) => {
  const router = useRouter();
  const [breadcrumbs, setBreadcrumbsState] = useState<FolderBreadcrumb[]>([]);
  const [hasNavigationContext, setHasNavigationContext] = useState(false);

  // Clear navigation context when leaving library pages
  useEffect(() => {
    const isLibraryPage =
      router.pathname === "/library" ||
      router.pathname.startsWith("/library/folders");

    if (!isLibraryPage) {
      setBreadcrumbsState([]);
      setHasNavigationContext(false);
    }
  }, [router.pathname]);

  const navigateToSubfolder = useCallback((folder: FolderBreadcrumb) => {
    setBreadcrumbsState((prev) => [...prev, folder]);
    setHasNavigationContext(true);
  }, []);

  const navigateToBreadcrumb = useCallback((libraryItemId: string) => {
    setBreadcrumbsState((prev) => {
      const index = prev.findIndex((b) => b.id === libraryItemId);
      if (index === -1) {
        // Navigating to Library (root), clear all breadcrumbs
        return [];
      }
      // Keep breadcrumbs up to and including the clicked item
      return prev.slice(0, index + 1);
    });
    setHasNavigationContext(true);
  }, []);

  const setBreadcrumbs = useCallback((newBreadcrumbs: FolderBreadcrumb[]) => {
    setBreadcrumbsState(newBreadcrumbs);
    // When setting breadcrumbs from computed path, we don't have navigation context
    // until the user starts navigating within the app
  }, []);

  const clearNavigationContext = useCallback(() => {
    setBreadcrumbsState([]);
    setHasNavigationContext(false);
  }, []);

  const contextValue: FolderBreadcrumbContextValue = useMemo(
    () => ({
      breadcrumbs,
      navigateToSubfolder,
      navigateToBreadcrumb,
      setBreadcrumbs,
      hasNavigationContext,
      clearNavigationContext,
    }),
    [
      breadcrumbs,
      navigateToSubfolder,
      navigateToBreadcrumb,
      setBreadcrumbs,
      hasNavigationContext,
      clearNavigationContext,
    ],
  );

  return (
    <FolderBreadcrumbContext.Provider value={contextValue}>
      {children}
    </FolderBreadcrumbContext.Provider>
  );
};

export const useFolderBreadcrumbContext = (): FolderBreadcrumbContextValue => {
  const context = useContext(FolderBreadcrumbContext);
  if (!context) {
    throw new Error(
      "useFolderBreadcrumbContext must be used within a FolderBreadcrumbProvider",
    );
  }
  return context;
};
